import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { RecentFile } from '../SessionManager';

interface DeletionInfoIconProps {
  file: RecentFile;
  size?: 'small' | 'medium';
}

const DeletionInfoIcon: React.FC<DeletionInfoIconProps> = ({ file, size = 'medium' }) => {
  // Get the actual upload time from file data
  const getUploadTime = (file: RecentFile) => {
    if (file.uploadTime) {
      return new Date(file.uploadTime);
    }
    return new Date();
  };

  // Calculate time until deletion (7 days from upload)
  const calculateTimeUntilDeletion = (file: RecentFile) => {
    const uploadTime = getUploadTime(file);
    const deleteTime = new Date(uploadTime.getTime() + 7 * 24 * 60 * 60 * 1000); // 7 days after upload
    const now = new Date();
    const timeLeft = deleteTime.getTime() - now.getTime();
    
    if (timeLeft <= 0) {
      return { expired: true, days: 0, hours: 0, minutes: 0 };
    }
    
    const days = Math.floor(timeLeft / (24 * 60 * 60 * 1000));
    const hours = Math.floor((timeLeft % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
    const minutes = Math.floor((timeLeft % (60 * 60 * 1000)) / (60 * 1000));
    
    return { expired: false, days, hours, minutes };
  };

  // Get icon color based on urgency
  const getDeletionIconColor = (timeUntilDeletion: any) => {
    if (timeUntilDeletion.expired) return '#f44336'; // Red
    if (timeUntilDeletion.days <= 1) return '#ff9800'; // Orange
    if (timeUntilDeletion.days <= 3) return '#ffc107'; // Yellow
    return '#4caf50'; // Green
  };

  // Format time until deletion for display
  const formatTimeUntilDeletion = (timeUntilDeletion: any) => {
    if (timeUntilDeletion.expired) return 'File expired - should be deleted';
    if (timeUntilDeletion.days > 0) return `${timeUntilDeletion.days} day${timeUntilDeletion.days !== 1 ? 's' : ''} left`;
    if (timeUntilDeletion.hours > 0) return `${timeUntilDeletion.hours} hour${timeUntilDeletion.hours !== 1 ? 's' : ''} left`;
    return `${timeUntilDeletion.minutes} minute${timeUntilDeletion.minutes !== 1 ? 's' : ''} left`;
  };

  // Only show for user-uploaded files
  if (!file.isUserUploaded) {
    return null;
  }

  const timeUntilDeletion = calculateTimeUntilDeletion(file);
  const iconSize = size === 'small' ? '16px' : '18px';
  const containerSize = size === 'small' ? '20px' : '24px';

  return (
    <Tooltip 
      title={
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'white' }}>
            {formatTimeUntilDeletion(timeUntilDeletion)}
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', mt: 0.5, display: 'block' }}>
            Deletion: {new Date(getUploadTime(file).getTime() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString()}
          </Typography>
        </Box>
      }
      placement="bottom"
      arrow
    >
      <Box 
        className="deletion-info-icon"
        sx={{ 
          color: getDeletionIconColor(timeUntilDeletion),
          cursor: 'help',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: containerSize,
          height: containerSize,
          borderRadius: '50%',
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.05)',
            transform: 'scale(1.1)'
          }
        }}
      >
        <svg width={iconSize} height={iconSize} fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"></path>
        </svg>
      </Box>
    </Tooltip>
  );
};

export default DeletionInfoIcon;
